"use client";

// Section animée de la fiche Hermes Agent : la mémoire persistante.
//
// L'argument est difficile à faire passer par un paragraphe (« il se souvient »
// se dit vite, se prouve mal), donc on le met en scène : trois moments d'une
// même relation de travail, séparés par des semaines puis des mois. Le visiteur
// choisit le moment, la conversation se rejoue, et l'encart « ce qu'il a
// retenu » montre ce que l'agent a gardé sans qu'on le lui redemande.
//
// Pas d'AnimatePresence ici : en framer-motion 6.5 la sortie coordonnée
// (mode="wait") n'est pas garantie. On rejoue l'entrée via une `key` qui change
// avec le moment sélectionné, ce qui suffit et reste sobre.

import { useState } from "react";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const MOMENTS = [
    {
        chip: "Ha_mem_m1_chip",
        you: "Ha_mem_m1_you",
        agent: "Ha_mem_m1_agent",
        memory: "Ha_mem_m1_memory",
    },
    {
        chip: "Ha_mem_m2_chip",
        you: "Ha_mem_m2_you",
        agent: "Ha_mem_m2_agent",
        memory: "Ha_mem_m2_memory",
    },
    {
        chip: "Ha_mem_m3_chip",
        you: "Ha_mem_m3_you",
        agent: "Ha_mem_m3_agent",
        memory: "Ha_mem_m3_memory",
    },
];

export default function HaMemory() {
    const t = useTranslations();
    const [current, setCurrent] = useState(0);
    const moment = MOMENTS[current];

    return (
        <section id="ha-memory" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Ha_mem_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Ha_mem_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-6 sm:p-10">
                {/* Glows (indigo Hermes) */}
                <div className="absolute -top-24 -right-20 w-[460px] h-[460px] bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-violet-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 rounded-full bg-indigo-500/15 ring-1 ring-indigo-400/30 px-3 py-1 mb-6">
                        <SolarIcon
                            name="cursor-bold-duotone"
                            className="w-3.5 h-3.5 text-indigo-300"
                        />
                        <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-300">
                            {t("Ha_mem_badge")}
                        </span>
                    </div>

                    {/* Frise des moments */}
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                        {MOMENTS.map((item, index) => {
                            const isActive = index === current;

                            return (
                                <button
                                    key={item.chip}
                                    onClick={() => setCurrent(index)}
                                    className={`rounded-full px-4 py-2 text-[13px] font-medium transition-colors duration-200 ${
                                        isActive
                                            ? "bg-indigo-500 text-white ring-1 ring-indigo-400/60"
                                            : "bg-white/[0.06] text-gray-300 ring-1 ring-white/10 hover:bg-white/[0.1]"
                                    }`}
                                >
                                    {t(item.chip)}
                                </button>
                            );
                        })}
                    </div>

                    {/* Conversation du moment sélectionné */}
                    <motion.div
                        key={current}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, ease: "easeOut" }}
                        className="mt-7 flex flex-col gap-4"
                    >
                        {/* Ce que vous écrivez */}
                        <div className="flex flex-col items-end">
                            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 mb-1.5">
                                {t("Ha_mem_you_label")}
                            </span>
                            <div className="max-w-[90%] sm:max-w-[75%] rounded-2xl rounded-tr-md bg-white/[0.08] ring-1 ring-white/10 px-5 py-3.5">
                                <p className="text-[15px] text-gray-100 leading-snug mb-0">
                                    {t(moment.you)}
                                </p>
                            </div>
                        </div>

                        {/* Ce que répond Hermes */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.35,
                                ease: "easeOut",
                                delay: 0.18,
                            }}
                            className="flex flex-col items-start"
                        >
                            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-indigo-300 mb-1.5">
                                {t("Ha_mem_agent_label")}
                            </span>
                            <div className="max-w-[90%] sm:max-w-[75%] rounded-2xl rounded-tl-md bg-indigo-500/15 ring-1 ring-indigo-400/30 px-5 py-3.5">
                                <p className="text-[15px] text-gray-100 leading-snug mb-0">
                                    {t(moment.agent)}
                                </p>
                            </div>
                        </motion.div>

                        {/* Ce qu'il a retenu */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.35,
                                ease: "easeOut",
                                delay: 0.34,
                            }}
                            className="mt-2 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-4"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <SolarIcon
                                    name="bookmark-bold-duotone"
                                    className="w-4 h-4 text-indigo-300"
                                />
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-indigo-300">
                                    {t("Ha_mem_memory_label")}
                                </span>
                            </div>
                            <p className="text-[15px] text-white font-medium leading-snug mb-0">
                                {t(moment.memory)}
                            </p>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            <p className="mt-6 text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Ha_mem_caption")}
            </p>
        </section>
    );
}
